import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Building2, Flag, Clock, XCircle, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const STATUS_BADGE = {
  pending: { label: "En attente", icon: Clock, cls: "bg-muted text-foreground" },
  approved: { label: "Validée", icon: CheckCircle2, cls: "bg-[hsl(var(--success))/0.14] text-foreground border-[hsl(var(--success))/0.35]" },
  rejected: { label: "Refusée", icon: XCircle, cls: "bg-[hsl(var(--destructive))/0.12] text-foreground border-[hsl(var(--destructive))/0.35]" },
  revoked: { label: "Révoquée", icon: XCircle, cls: "bg-muted text-muted-foreground" },
};

export default function MyStructuresPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { nav("/connexion?next=/mes-structures"); return; }
    (async () => {
      try {
        const res = await api.get("/entity-managers/mine");
        setItems(res.data?.items || []);
      } finally { setLoading(false); }
    })();
  }, [user, nav]);

  const approved = items.filter((i) => i.status === "approved");
  const pending = items.filter((i) => i.status === "pending");
  const others = items.filter((i) => ["rejected", "revoked"].includes(i.status));

  const renderItem = (it) => {
    const S = STATUS_BADGE[it.status] || STATUS_BADGE.pending;
    const Icon = it.entity_type === "club" ? Building2 : Flag;
    const path = it.entity_type === "club" ? `/clubs/${it.entity_slug || it.entity_id}` : `/federations/${it.entity_slug || it.entity_id}`;
    return (
      <Card key={it.id} className="aito-card-hover" data-testid={`my-structure-row-${it.id}`}>
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Icon className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />
            <div>
              <div className="font-medium flex items-center gap-2">
                {it.entity_name || "Structure"}
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${S.cls}`}>{S.label}</Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                {it.role_label || "—"} · {it.entity_type === "club" ? "Club" : "Fédération"}
              </div>
            </div>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to={path}>{it.status === "approved" ? "Gérer" : "Voir"}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="my-structures-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-display font-semibold">Mes structures</h1>
        <p className="text-sm text-muted-foreground">Les clubs et fédérations que vous gérez ou avez revendiqués.</p>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <>
          <section className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Structures gérées ({approved.length})</h2>
            {approved.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Vous ne gérez aucune structure pour le moment.</p>
            ) : approved.map(renderItem)}
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Demandes en attente ({pending.length})</h2>
            {pending.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aucune demande en attente.</p>
            ) : pending.map(renderItem)}
          </section>

          {others.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Historique ({others.length})</h2>
              {others.map(renderItem)}
            </section>
          )}
        </>
      )}
    </div>
  );
}
